/**
 * User Management — the site-user console.
 *
 * One email lookup up front, then every operation acts on the loaded account.
 * The email lives in the URL (`?email=`) so a support ticket can link straight
 * to a user, and a refresh doesn't lose the context.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/ui/sonner";
import {
  AtSign,
  ClipboardCopy,
  CircleAlert,
  Loader2,
  RefreshCw,
  Search,
  UserPlus,
} from "lucide-react";
import {
  REFUND_WINDOW_DAYS,
  accountRevenue,
  accountSummary,
  providerOrNull,
  type UserAccount,
  type UserBillingResponse,
} from "../../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { BillingTab } from "./billing-tab";
import { DangerTab } from "./danger-tab";
import { HistoryTab } from "./history-tab";
import { ActivityTab } from "./activity-tab";
import { OverviewTab } from "./overview-tab";
import { SubscriptionTab } from "./subscription-tab";
import { ProviderBadge } from "./provider-badge";
import { PlanBadge } from "./plan-badge";
import { UsersHome } from "./home";
import {
  clearRecent,
  pushRecent,
  readRecent,
  type RecentLookup,
} from "./recent-lookups";
import { Detail } from "./parts";
import { describeError, formatDate, formatMoney, relativeDate } from "./format";
import { Copyable } from "./copyable";
import { EmailTag } from "./email-tag";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "subscription", label: "Subscription & add-ons" },
  { key: "billing", label: "Billing" },
  { key: "history", label: "History" },
  { key: "activity", label: "Activity" },
  { key: "account", label: "Account actions" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export default function UserManagementPage() {
  const [params, setParams] = useSearchParams();
  const email = params.get("email") ?? "";
  const [draft, setDraft] = useState(email);
  const [tab, setTab] = useState<TabKey>("overview");

  const [recent, setRecent] = useState<RecentLookup[]>(() => readRecent());

  const lookup = useQuery({
    queryKey: ["site-user", email],
    queryFn: () => api.siteUsers.lookup(email),
    enabled: email.length > 0,
    retry: false,
  });

  // Remember the address that actually resolved, not every typo typed into the
  // box — the list is only useful if every row loads something.
  const resolved = lookup.data?.account.email;
  useEffect(() => {
    if (resolved) setRecent(pushRecent(resolved));
  }, [resolved]);

  // Support is often handed a receipt, not an address — anything that is not an
  // email is resolved through the payment providers first.
  const resolve = useMutation({
    mutationFn: (q: string) => api.siteUsers.resolve(q),
    onSuccess: (hit) => {
      toast.success(`Found ${hit.email} via ${hit.via}`);
      setDraft(hit.email);
      setTab("overview");
      setParams({ email: hit.email });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = draft.trim();
    if (!next) return;
    if (next.includes("@")) {
      setTab("overview");
      setParams({ email: next.toLowerCase() });
    } else {
      resolve.mutate(next);
    }
  };

  const account = lookup.data?.account ?? null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-sm text-muted-foreground">
            Look up a user once, then act on them — subscriptions, add-ons,
            email, billing.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/dashboard/users/new">
            <UserPlus className="mr-2 h-4 w-4" />
            Create user
          </Link>
        </Button>
      </header>

      <form onSubmit={submit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="user@example.com, a FastSpring order ID, or a Stripe id"
            className="pl-9"
            autoFocus
          />
        </div>
        <Button
          type="submit"
          disabled={!draft.trim() || lookup.isFetching || resolve.isPending}
        >
          {lookup.isFetching || resolve.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Look up
        </Button>
      </form>

      {!email && (
        <UsersHome
          recent={recent}
          onPick={(picked) => {
            setDraft(picked);
            setTab("overview");
            setParams({ email: picked });
          }}
          onClear={() => {
            clearRecent();
            setRecent([]);
          }}
        />
      )}

      {email && lookup.isError && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <CircleAlert className="mt-0.5 h-4 w-4 text-destructive" />
          <div>
            <p className="font-medium">{describeError(lookup.error)}</p>
            <div className="mt-1 flex items-center gap-3">
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => lookup.refetch()}
              >
                Try again
              </Button>
              {lookup.error instanceof ApiError && lookup.error.status === 404 && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/dashboard/users/new?email=${encodeURIComponent(email)}`}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Create {email}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {account && lookup.data?.isAlias && (
        <div className="flex items-start gap-3 rounded-xl border border-warning/50 bg-warning/10 p-4 text-sm">
          <AtSign className="mt-0.5 h-4 w-4" />
          <p>
            <span className="font-medium">{lookup.data.requestedEmail}</span> is
            a previous address of this account. It now signs in as{" "}
            <span className="font-medium">{account.email}</span>.
          </p>
        </div>
      )}

      {account && (
        <>
          <IdentityHeader
            account={account}
            refreshing={lookup.isFetching}
            onRefresh={() => lookup.refetch()}
          />

          <nav className="flex flex-wrap gap-1 border-b">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors",
                  tab === t.key
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {t.label}
              </button>
            ))}
          </nav>

          {tab === "overview" && <OverviewTab account={account} />}
          {tab === "subscription" && <SubscriptionTab account={account} />}
          {tab === "billing" && <BillingTab account={account} />}
          {tab === "history" && <HistoryTab account={account} />}
          {tab === "activity" && <ActivityTab account={account} />}
          {tab === "account" && <DangerTab account={account} />}
        </>
      )}
    </div>
  );
}

/** Always-visible identity strip, so no tab has to repeat who we're acting on. */
function IdentityHeader({
  account,
  refreshing,
  onRefresh,
}: {
  account: UserAccount;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  // Whatever the Billing tab has already fetched — the header never triggers a
  // provider call of its own.
  const billing = useQuery({
    queryKey: ["site-user-billing", account.email],
    queryFn: () => api.siteUsers.billing(account.email),
    retry: false,
  });
  const revenue = billing.data ? accountRevenue(billing.data.snapshots) : null;

  /**
   * Uses the billing already in the cache rather than fetching: the summary is
   * a convenience, and it must not make the header wait on two providers.
   */
  const copySummary = async () => {
    const billing = queryClient.getQueryData<UserBillingResponse>([
      "site-user-billing",
      account.email,
    ]);
    try {
      await navigator.clipboard.writeText(
        accountSummary(account, billing?.snapshots ?? [])
      );
    } catch {
      toast.error("Could not reach the clipboard");
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const expired =
    account.expiration != null && new Date(account.expiration) < new Date();
  // Previous addresses matter to support: the user's payments, receipts and
  // provider accounts are usually still under one of them.
  const previousEmails = account.emailHistory
    .filter((e) => !e.isCurrent && e.email.toLowerCase() !== account.email.toLowerCase())
    .map((e) => e.email);

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">
            <Copyable value={account.email} />
          </h2>
          <PlanBadge plan={account.productPlanName} />
          {expired && <Badge variant="warning">Expired</Badge>}
          {previousEmails.length > 0 && (
            <Badge
              variant="warning"
              title={`Previously: ${previousEmails.join(", ")}`}
            >
              <AtSign className="mr-1 h-3 w-3" />
              Email changed ×{previousEmails.length}
            </Badge>
          )}
          {account.tag && <Badge variant="outline">{account.tag}</Badge>}
        </div>
        <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={copySummary}>
          <ClipboardCopy className="mr-2 h-4 w-4" />
          {copied ? "Copied" : "Copy for ticket"}
        </Button>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw
            className={cn("mr-2 h-4 w-4", refreshing && "animate-spin")}
          />
          Refresh
        </Button>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Detail label="User ID">
          {account.uid ? <Copyable value={account.uid} mono /> : "—"}
        </Detail>
        <Detail label="Expires">
          {formatDate(account.expiration)}
          {relativeDate(account.expiration) && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {relativeDate(account.expiration)}
            </span>
          )}
        </Detail>
        <Detail label="Registered">{formatDate(account.registrationDate)}</Detail>
        <Detail label="Origin">{account.origin ?? "—"}</Detail>
        <Detail label="Next charge via">
          {providerOrNull(account.nextChargePaymentProvider) ? (
            <ProviderBadge provider={account.nextChargePaymentProvider} />
          ) : (
            "—"
          )}
        </Detail>
        <Detail label="Run rate / month">
          {revenue == null ? (
            <span className="text-muted-foreground">…</span>
          ) : revenue.runRate.length === 0 ? (
            "—"
          ) : (
            <span
              className="flex flex-wrap gap-x-2"
              title="Live subscriptions spread over a month. Provider prices may exclude tax, so an actual charge can be higher."
            >
              {revenue.runRate.map((m) => (
                <span key={m.currency}>{formatMoney(m.amount, m.currency)}</span>
              ))}
            </span>
          )}
        </Detail>
        <Detail label="Paid to date (incl. tax)">
          {revenue == null ? (
            <span className="text-muted-foreground">…</span>
          ) : revenue.lifetime.length === 0 && revenue.atRisk.length === 0 ? (
            "—"
          ) : (
            <span
              className="flex flex-wrap gap-x-2"
              title={
                revenue.monthsAsCustomer != null
                  ? `Charges minus refunds, as billed (tax included). First payment ${revenue.firstPayment?.slice(0, 10)} — ${revenue.monthsAsCustomer} months a customer`
                  : "Charges minus refunds, as billed (tax included)"
              }
            >
              {revenue.lifetime.map((m) => (
                <span key={m.currency}>{formatMoney(m.amount, m.currency)}</span>
              ))}
              {revenue.monthsAsCustomer != null && (
                <span className="text-xs font-normal text-muted-foreground">
                  over {revenue.monthsAsCustomer} mo
                </span>
              )}
              {revenue.atRisk.length > 0 && (
                <span
                  className="text-xs font-normal text-warning"
                  title={`Cancelled within ${REFUND_WINDOW_DAYS} days — probably refunded, so not counted`}
                >
                  (+
                  {revenue.atRisk
                    .map((m) => formatMoney(m.amount, m.currency))
                    .join(" ")}{" "}
                  pending refund)
                </span>
              )}
            </span>
          )}
        </Detail>
      </dl>
      {previousEmails.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
          <span>Addresses on this account:</span>
          {[account.email, ...previousEmails].map((e, i) => (
            <EmailTag
              key={e}
              email={e}
              all={[account.email, ...previousEmails]}
              current={i === 0}
            />
          ))}
          <span>— billing is searched under all of them.</span>
        </div>
      )}
    </div>
  );
}
